import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import {
  ChevronDown, ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import HeroAnimation from './HeroAnimation';
import { scrollToSection } from '../../utils/scroll';

interface HeroProps {
  theme?: 'dark' | 'light';
  activeTab: 'crm' | 'ia';
  setActiveTab: (tab: 'crm' | 'ia') => void;
}

const Hero: React.FC<HeroProps> = ({ theme = 'dark' }) => {
  const [phoneCycle, setPhoneCycle] = useState(0);
  const { t } = useTranslation();

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.065, delayChildren: 0.8 } }
  };

  const itemVariants: Variants = {
    hidden: { y: 15, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] } }
  };

  // El teléfono entra SOLO con opacidad (sin translateY). El stage vive en 1080px
  // escalado a ~0.48 y la pantalla es dpr:1, así que cualquier traslación durante
  // la entrada lo renderiza en posiciones sub-pixel y se ve borroso al inicio.
  const phoneVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <section className="hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', padding: 'clamp(56px, 7vh, 80px) 0 clamp(40px, 6vh, 64px)' }}>
      <style>{`
        .hero-new-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          z-index: 10;
          min-height: calc(100vh - clamp(84px, 10vh, 112px) - clamp(40px, 6vh, 64px));
          width: 100%;
          min-width: 0;
        }

        .hero-split-layout {
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          align-items: center;
          gap: clamp(2rem, 4vw, 5rem);
          width: 100%;
          min-width: 0;
        }

        .hero-left-col {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 1.8rem;
          min-width: 0;
        }

        .hero-right-col {
          display: flex;
          align-items: center;
          justify-content: center;
          height: min(80vh, 780px);
          min-height: 440px;
          width: 100%;
          min-width: 0;
        }


        .hero-title-new {
          font-family: var(--font-heading);
          font-size: clamp(1.8rem, 4vw, 4rem);
          line-height: 1.1;
          letter-spacing: -0.04em;
          margin: 0;
          color: var(--text-primary);
          font-weight: 300;
          width: 100%;
          max-width: 100%;
          overflow-wrap: break-word;
        }

        .hero-title-new .grad-text {
          background: none !important;
          -webkit-text-fill-color: inherit !important;
          color: inherit !important;
          font-weight: 700;
        }

        .hero-underline {
          font-weight: 700;
          background: linear-gradient(135deg, #00f0ff 0%, #9d6bff 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }

        .hero-sub-new {
          font-family: var(--font-body);
          font-size: clamp(0.9rem, 1.4vw, 1.05rem);
          color: var(--text-secondary);
          margin: 0;
          line-height: 1.6;
        }

        .hero-sub-new .grad-text {
          background: none !important;
          -webkit-text-fill-color: inherit !important;
          color: inherit !important;
          font-weight: 700;
        }

        .explore-label {
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: var(--text-primary);
          opacity: 0.6;
          margin: 0;
        }

        .trial-request-container {
          display: flex;
          align-items: center;
          background: #111;
          border: 1px solid #333;
          border-radius: 10px;
          padding: 4px;
          width: 100%;
          max-width: 420px;
          box-shadow: 0 10px 30px -10px rgba(0,0,0,0.5);
          transition: all 0.3s ease;
        }

        [data-theme='light'] .trial-request-container {
          background: #fff;
          border: 1px solid #e5e5e5;
          box-shadow: 0 10px 30px -10px rgba(0,0,0,0.05);
        }

        .trial-input {
          flex: 1;
          background: transparent;
          border: none;
          padding: 10px 15px;
          color: inherit;
          font-family: var(--font-body);
          font-size: 0.9rem;
          outline: none;
          min-width: 0;
        }

        .trial-btn {
          /* En tema oscuro el fondo de la página ya es negro puro (#000) — un botón
             negro se volvía invisible. Se invierte por tema, como hace Apple. */
          background: #fff;
          color: #000;
          border: none;
          padding: 10px 20px;
          border-radius: 980px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.2s ease;
        }

        .trial-btn:hover {
          background: #e8e8ed;
        }

        [data-theme='light'] .trial-btn {
          background: #000;
          color: #fff;
        }

        [data-theme='light'] .trial-btn:hover {
          background: #1d1d1f;
        }

        .trial-meta {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          width: 100%;
          max-width: 420px;
        }

        .trial-no-spam {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.85rem;
          color: var(--text-secondary);
          opacity: 0.7;
        }

        .trial-no-spam span {
          white-space: nowrap;
        }

        .trial-no-spam svg {
          flex-shrink: 0;
        }

        @media (max-width: 420px) {
          .trial-no-spam { font-size: 0.68rem; }
        }

        .social-proof-row {
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .sp-avatars {
          display: flex;
        }

        .sp-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 1px solid #000;
          margin-left: -6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.55rem;
          font-weight: 700;
          color: #fff;
        }

        .sp-avatar:first-child { margin-left: 0; background: #7c3aed; }
        .sp-avatar:nth-child(2) { background: #0ea5e9; }
        .sp-avatar:nth-child(3) { background: #10b981; }
        .sp-avatar:nth-child(4) { background: #f59e0b; }

        .sp-stars {
          color: #f59e0b;
          font-size: 0.85rem;
          letter-spacing: -1px;
        }

        .sp-text {
          font-size: 0.85rem;
          color: var(--text-secondary);
          opacity: 0.75;
        }

        @media (max-width: 1024px) {
          .trial-meta { align-items: center; }
          .trial-no-spam { justify-content: center; }
          .social-proof-row { justify-content: center; }
        }

        .product-selector {
          display: flex;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          padding: 5px;
          border-radius: 10px;
          position: relative;
          box-shadow: 0 10px 40px -10px rgba(0,0,0,0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        [data-theme='light'] .product-selector {
          background: rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .tab-btn {
          padding: 10px 0;
          border: none;
          background: transparent;
          color: var(--text-secondary);
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          position: relative;
          z-index: 1;
          transition: color 0.3s ease;
        }

        .tab-btn.active { color: #000; }
        [data-theme='light'] .tab-btn.active { color: #fff; }

        .tab-glow {
          position: absolute;
          top: 5px; bottom: 5px; left: 5px;
          border-radius: 6px;
          background: #fff;
          z-index: 0;
        }

        [data-theme='light'] .tab-glow {
          background: #000;
        }

        .scroll-indicator {
          position: absolute;
          bottom: -2.5rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.4rem;
          color: var(--text-primary);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          opacity: 0.8;
        }

        .scroll-arrow { animation: bounce 2s infinite; }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-5px); }
          60% { transform: translateY(-3px); }
        }

        @media (max-width: 1024px) {
          .hero-split-layout {
            grid-template-columns: 1fr;
          }
          .hero-right-col { height: min(65vh, 560px); min-height: 380px; }
          .hero-new-container { align-items: center; }
          .hero-left-col { align-items: center; text-align: center; }
          .hero-title-new { text-align: center; }
          .hero-sub-new { text-align: center; }
          .explore-label { text-align: center; }
        }

        @media (max-width: 480px) {
          .hero-right-col { height: min(52vh, 420px); min-height: 300px; }
          .trial-request-container {
            flex-direction: column;
            background: transparent !important;
            border: none;
            box-shadow: none;
            gap: 10px;
          }
          .trial-input {
            width: 100%;
            background: #111;
            border: 1px solid #333;
            border-radius: 10px;
            padding: 14px;
            text-align: center;
          }
          [data-theme='light'] .trial-input {
            background: #fff;
            border: 1px solid #e5e5e5;
            color: #000;
          }
          .trial-btn { width: 100%; padding: 14px; border-radius: 980px; }
          .hero-sub-new { font-size: 0.95rem; line-height: 1.4; }
          .scroll-indicator { bottom: -1.5rem; }
        }
      `}</style>

      <motion.div className="hero-new-container main-section-layout" variants={containerVariants} initial="hidden" animate="visible">

        <div className="hero-split-layout">
          {/* Columna izquierda */}
          <div className="hero-left-col">
            <motion.h1 className="hero-title-new" variants={itemVariants}>
              <span>{t('hero.title_1_start')}<span className="hero-underline">{t('hero.title_1_highlight')}</span></span><br />
              <span>{t('hero.title_2')} <span className="hero-underline">{t('hero.title_money')}</span></span>
            </motion.h1>

            <motion.p className="hero-sub-new" variants={itemVariants}>
              {t('hero.subtitle_start')}
              <span className="grad-text">{t('hero.subtitle_highlight')}</span>
            </motion.p>

            <motion.div variants={itemVariants} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.55rem', width: '100%', maxWidth: '560px' }}>
              {/* Envoltorio que se ajusta al ancho de su hijo más ancho (la frase, que
                  queda intacta pegada a la izquierda) para poder centrar el botón
                  respecto a ESA frase en vez de respecto a todo el contenedor. */}
              <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '0.55rem', width: 'fit-content', maxWidth: '100%' }}>
                <button
                  className="trial-btn"
                  onClick={() => scrollToSection('#cta-final')}
                  style={{ width: 'auto', padding: '15px 26px', borderRadius: 980, fontSize: '0.95rem', fontFamily: 'var(--font-heading)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
                >
                  {t('hero.trial_button')}
                  <ArrowRight size={17} />
                </button>

                <div className="trial-no-spam">
                  <svg width="11" height="13" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="5" width="9" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M3 5V3.5a2.5 2.5 0 015 0V5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  <span>{t('hero.trial_no_spam')} · {t('hero.trial_after')}</span>
                </div>
              </div>

              <div className="social-proof-row">
                <div className="sp-avatars">
                  <div className="sp-avatar">RC</div>
                  <div className="sp-avatar">MV</div>
                  <div className="sp-avatar">AP</div>
                  <div className="sp-avatar">+</div>
                </div>
                <span className="sp-stars">★★★★★</span>
                <span className="sp-text">{t('hero.social_proof')}</span>
              </div>
            </motion.div>
          </div>

          {/* Columna derecha: animación iPhone RurayHUB */}
          <motion.div
            className="hero-right-col"
            variants={phoneVariants}
            style={{ willChange: 'opacity' }}
            onViewportEnter={() => setPhoneCycle(c => c + 1)}
            viewport={{ amount: 0.3 }}
          >
            <HeroAnimation key={phoneCycle} theme={theme} />
          </motion.div>
        </div>

        <motion.div className="scroll-indicator" initial={{ opacity: 0 }} animate={{ opacity: theme === 'light' ? 0.5 : 0.8 }} transition={{ delay: 2.5, duration: 1 }}>
          <span>{t('hero.scroll_hint', 'Desliza para descubrir')}</span>
          <ChevronDown className="scroll-arrow" size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
